// Compiles the layout into the flow network (PRD section 17: "el interior de cada casilla se compila
// en un grafo"). Stores at the ports split the world into lines: stockless chains of belts, machines,
// extraction and routes that each pour into one store. Every node hands its output to exactly one
// neighbour, so each line is a tree whose root feeds the store; inputs may merge, outputs never split.
import type { ItemId } from './data';
import { across, idOrder, keyOf, readingOrder, SIDES, type Side, type Socket } from './geometry';
import { machineInput, machineOutput, routeThroughput, type MachineState, type PortRef, type RouteState, type Store, type TileState, type World } from './world';

export type FlowNode =
  | { readonly kind: 'source'; readonly store: Store; readonly port: PortRef }
  | { readonly kind: 'belt'; readonly tile: string; readonly key: string; readonly capacity: number }
  | { readonly kind: 'machine'; readonly tile: string; readonly machine: MachineState }
  | { readonly kind: 'extract'; readonly tile: string; readonly item: ItemId; readonly rate: number }
  | { readonly kind: 'route'; readonly route: RouteState; readonly capacity: number };

export interface Line {
  readonly id: string;
  /** Every node comes after the nodes feeding it; the last one pours into the sink. */
  readonly nodes: readonly FlowNode[];
  /** Indexes of the nodes feeding each node. */
  readonly children: readonly (readonly number[])[];
  readonly sink: Store;
  readonly sinkPort: PortRef;
}

export interface Network {
  readonly layoutVersion: number;
  /** Lines in solving order: the lines filling a store come before the lines drawing from it. */
  readonly lines: readonly Line[];
  /** Interior pieces that reach no exit (dead ends and loops): belt keys and machine ids, per tile. */
  readonly stranded: ReadonlyMap<string, { readonly belts: ReadonlySet<string>; readonly machines: ReadonlySet<string> }>;
}

export const socketKey = (s: Socket): string => `${keyOf(s.cell)}${s.side}`;

/** A piece of an interior that takes material in: a belt (any side but its exit) or a machine's input. */
export type Receiver = { readonly kind: 'belt'; readonly key: string } | { readonly kind: 'machine'; readonly machine: MachineState };

/** The edges of an interior where material changes hands, as the flow network links them. */
export interface InteriorSockets {
  /** What takes material entering a cell through a side, by `socketKey` of that cell and side. */
  readonly accepts: ReadonlyMap<string, Receiver>;
  /** Exit doors by `socketKey` of the border edge they sit on: material leaving through it pours into the door. */
  readonly exits: ReadonlyMap<string, string>;
  /** Entrance doors by `socketKey` of the border edge: they hand material into that cell through that side. */
  readonly entrances: ReadonlyMap<string, string>;
}

/** Belts of an interior in reading order, so every build of the network is the same. */
function sortedBelts(interior: NonNullable<TileState['interior']>): [string, Side][] {
  return [...interior.belts.entries()].sort((a, b) => readingOrder(a[0]) - readingOrder(b[0]));
}

function sortedMachines(interior: NonNullable<TileState['interior']>): MachineState[] {
  return [...interior.machines.values()].sort((a, b) => idOrder(a.id) - idOrder(b.id));
}

export function interiorSockets(tile: TileState): InteriorSockets {
  const accepts = new Map<string, Receiver>();
  const exits = new Map<string, string>();
  const entrances = new Map<string, string>();
  const interior = tile.interior;
  for (const port of tile.def.ports) {
    if (!port.edge) continue;
    (port.kind === 'out' ? exits : entrances).set(socketKey(port.edge), port.id);
  }
  if (!interior) return { accepts, exits, entrances };
  for (const [key, exit] of sortedBelts(interior)) {
    for (const side of SIDES) if (side !== exit) accepts.set(`${key}${side}`, { kind: 'belt', key });
  }
  for (const machine of sortedMachines(interior)) accepts.set(socketKey(machineInput(machine)), { kind: 'machine', machine });
  return { accepts, exits, entrances };
}

/** Where material leaving through `leaves` goes: the exit door on that edge, or the piece across it. */
export function downstream(sockets: InteriorSockets, leaves: Socket): { readonly exit: string } | { readonly receiver: Receiver } | undefined {
  const exit = sockets.exits.get(socketKey(leaves));
  if (exit !== undefined) return { exit };
  const receiver = sockets.accepts.get(socketKey(across(leaves)));
  return receiver ? { receiver } : undefined;
}

interface Draft {
  readonly node: FlowNode;
  /**
   * Where the node's output goes: an entrance hands material straight into the edge cell
   * (`enters`); belts and machines push it out of one of their cells (`leaves`).
   */
  readonly enters?: Socket;
  readonly leaves?: Socket;
  /** Index of the node it feeds, or the id of the exit port it pours into. */
  next?: number | { readonly sink: string };
}

type Stranded = Map<string, { readonly belts: Set<string>; readonly machines: Set<string> }>;

function interiorLines(tile: TileState, beltRate: number, lines: Line[], stranded: Stranded): void {
  const interior = tile.interior;
  if (!interior) return;
  const id = tile.def.id;
  const sockets = interiorSockets(tile);
  const drafts: Draft[] = [];
  const beltIndex = new Map<string, number>();
  const machineIndex = new Map<string, number>();

  for (const port of tile.def.ports) {
    const store = tile.stores.get(port.id);
    if (!port.edge || !store || port.kind !== 'in') continue;
    drafts.push({ node: { kind: 'source', store, port: { tile: id, port: port.id } }, enters: port.edge });
  }
  for (const [key, exit] of sortedBelts(interior)) {
    const [col = 0, row = 0] = key.split(',').map(Number);
    beltIndex.set(key, drafts.push({ node: { kind: 'belt', tile: id, key, capacity: beltRate }, leaves: { cell: [col, row], side: exit } }) - 1);
  }
  for (const machine of sortedMachines(interior)) {
    machineIndex.set(machine.id, drafts.push({ node: { kind: 'machine', tile: id, machine }, leaves: machineOutput(machine) }) - 1);
  }
  const indexOf = (receiver: Receiver): number | undefined => (receiver.kind === 'belt' ? beltIndex.get(receiver.key) : machineIndex.get(receiver.machine.id));

  // Link every node to the neighbour its output enters, or to the exit it pours into.
  drafts.forEach((draft, i) => {
    if (draft.enters) {
      const receiver = sockets.accepts.get(socketKey(draft.enters));
      const target = receiver && indexOf(receiver);
      if (target !== undefined) draft.next = target;
    } else if (draft.leaves) {
      const to = downstream(sockets, draft.leaves);
      if (to && 'exit' in to) draft.next = { sink: to.exit };
      else if (to) {
        const target = indexOf(to.receiver);
        if (target !== undefined && target !== i) draft.next = target;
      }
    }
  });

  const feeders: number[][] = drafts.map(() => []);
  drafts.forEach((draft, i) => {
    if (typeof draft.next === 'number') feeders[draft.next]?.push(i);
  });

  // Each exit is fed by at most one node: walk its tree upstream, then reverse so feeders come first.
  // Loops never reach an exit, so they are left out with the dead ends.
  const reached = new Set<number>();
  for (const port of tile.def.ports) {
    const sink = tile.stores.get(port.id);
    const root = drafts.findIndex((d) => typeof d.next === 'object' && d.next.sink === port.id);
    if (port.kind !== 'out' || !sink || root < 0) continue;
    const order: number[] = [root];
    for (let k = 0; k < order.length; k++) order.push(...(feeders[order[k] ?? 0] ?? []));
    order.reverse();
    const local = new Map(order.map((global, l) => [global, l]));
    lines.push({
      id: `${id}:${port.id}`,
      nodes: order.map((global) => (drafts[global] as Draft).node),
      children: order.map((global) => (feeders[global] ?? []).map((f) => local.get(f) ?? 0)),
      sink,
      sinkPort: { tile: id, port: port.id },
    });
    for (const global of order) reached.add(global);
  }

  const lost = { belts: new Set<string>(), machines: new Set<string>() };
  drafts.forEach((draft, i) => {
    if (reached.has(i)) return;
    if (draft.node.kind === 'belt') lost.belts.add(draft.node.key);
    if (draft.node.kind === 'machine') lost.machines.add(draft.node.machine.id);
  });
  stranded.set(id, lost);
}

/** Builds the flow network of the current layout. */
export function buildNetwork(world: World): Network {
  const base: Line[] = [];
  const stranded: Stranded = new Map();
  for (const tile of world.tiles.values()) {
    const resource = tile.def.resource;
    if (resource) {
      const port = tile.def.ports[0];
      const sink = port ? tile.stores.get(port.id) : undefined;
      if (port && sink) {
        base.push({
          id: `${tile.def.id}:extract`,
          nodes: [{ kind: 'extract', tile: tile.def.id, item: resource.item.id, rate: resource.rate }],
          children: [[]],
          sink,
          sinkPort: { tile: tile.def.id, port: port.id },
        });
      }
    }
    interiorLines(tile, world.data.belt.rate, base, stranded);
  }
  const routes = [...world.routes.values()].sort((a, b) => idOrder(a.id) - idOrder(b.id));
  for (const route of routes) {
    const source = world.tiles.get(route.from.tile)?.stores.get(route.from.port);
    const sink = world.tiles.get(route.to.tile)?.stores.get(route.to.port);
    if (!source || !sink) continue;
    base.push({
      id: `route:${route.id}`,
      nodes: [
        { kind: 'source', store: source, port: route.from },
        { kind: 'route', route, capacity: routeThroughput(route.vehicle, route.length) },
      ],
      children: [[], [0]],
      sink,
      sinkPort: route.to,
    });
  }
  return { layoutVersion: world.layoutVersion, lines: solvingOrder(base), stranded };
}

/**
 * Orders lines so that, within one tick, material poured into a store can leave it again:
 * a stable topological sort over "line A fills a store that line B draws from".
 * Lines caught in a loop of stores keep their base order after the rest.
 */
function solvingOrder(lines: readonly Line[]): Line[] {
  const sources = lines.map((line) => new Set(line.nodes.flatMap((n) => (n.kind === 'source' ? [n.store] : []))));
  const waiting = lines.map((_, i) => lines.filter((other, j) => j !== i && sources[i]?.has(other.sink)).length);
  const done = new Set<number>();
  const order: Line[] = [];
  while (order.length < lines.length) {
    let pick = lines.findIndex((_, i) => !done.has(i) && waiting[i] === 0);
    if (pick < 0) pick = lines.findIndex((_, i) => !done.has(i));
    const line = lines[pick];
    if (!line) break;
    done.add(pick);
    order.push(line);
    lines.forEach((_, j) => {
      if (!done.has(j) && sources[j]?.has(line.sink)) waiting[j] = (waiting[j] ?? 1) - 1;
    });
  }
  return order;
}
